import ReactJoyride, { CallBackProps, EVENTS } from 'react-joyride'
import { useState, useEffect } from 'react'
import { useWindowSize } from 'react-use'
import { useJoyrideContext } from '../../context/JoyrideContext'
import { welcomeSteps } from './JoyrideSteps/welcomeSteps'
import { getVisibleFeatureAnnouncements } from './JoyrideSteps/featureAnnouncementSteps'
import { useGlobalContext } from 'context/GlobalContext'

interface JoyrideStep {
  target: string
  placement?: string
  title?: string
  isFinalReminder?: boolean
  content: React.ReactNode
  disableBeacon?: boolean
  styles?: {
    options?: {
      width?: number
    }
  }
}

export const Joyride = () => {
  const { joyrideState, joyrideDispatch } = useJoyrideContext()
  const { height } = useWindowSize()
  const [previousAction, setPreviousAction] = useState<string | null>(null)
  const { globalState } = useGlobalContext()
  const { userInfo } = globalState

  // Add global CSS to ensure text is black in Joyride tooltips
  useEffect(() => {
    // Create a style element
    const styleElement = document.createElement('style')
    styleElement.innerHTML = `
      .react-joyride__tooltip *,
      [data-tour-elem="content"] *,
      [data-tour-elem="tooltip"] * {
        color: #000000 !important;
      }
    `
    document.head.appendChild(styleElement)

    return () => {
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement)
      }
    }
  }, [])

  // Filter feature steps by user role
  const filteredFeatureSteps: JoyrideStep[] =
    getVisibleFeatureAnnouncements(userInfo)

  // Combine welcome steps with filtered feature steps
  const combinedSteps: JoyrideStep[] = [
    ...welcomeSteps,
    ...filteredFeatureSteps,
  ]

  const steps = combinedSteps.map((step) => {
    return {
      ...step,
      target: step.target || 'body',
      placement: step.placement || 'center',
      disableBeacon: step.disableBeacon ?? false,
      content: step.content,
    }
  })

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { action, type } = data

    if (action === 'close' || action === 'reset') {
      joyrideDispatch({ type: 'JOYRIDE_ALL' })
      setPreviousAction('close')
      return
    }

    if (type === EVENTS.TARGET_NOT_FOUND && previousAction !== 'close') {
      joyrideDispatch({ type: 'JOYRIDE_ALL' })

      setTimeout(() => {
        joyrideDispatch({ type: 'JOYRIDE_ALL' })
      }, 0)
    }
  }

  // Only render if we have steps and showAll is true
  if (steps.length === 0) {
    return null
  }

  return (
    <ReactJoyride
      showProgress
      continuous
      disableOverlayClose
      run={joyrideState.showAll}
      styles={{
        options: {
          arrowColor: '#3d70d6',
          primaryColor: '#3d70d6',
          width: 1200,
          backgroundColor: '#ffffff',
          textColor: '#000000',
          zIndex: 1000,
        },
        tooltip: {
          maxHeight: `${height + 100}px`,
          overflowY: 'auto',
          padding: '15px',
          border: 'none',
          boxShadow: 'none',
          backgroundColor: '#ffffff',
          color: '#000000',
        },
        tooltipContent: {
          padding: '20px 0',
          border: 'none',
          boxShadow: 'none',
          color: '#000000',
        },
        tooltipContainer: {
          textAlign: 'left',
          border: 'none',
          boxShadow: 'none',
          color: '#000000',
        },
      }}
      callback={handleJoyrideCallback}
      steps={steps as any}
      spotlightPadding={0}
      locale={{
        last: 'End',
      }}
    />
  )
}
